import type { Issue } from '../types/issue';

// Fix-Version-Analyse: Extraktion aller einzigartigen Fix-Version-Werte aus
// den Rows, plus strukturiertes Parsing (Jahr / Release / SP / Suffix), damit
// wir sinnvoll sortieren und gruppieren können.
//
// Beispiele:
//   „2026 R2"               → year=2026, release=2, sp=null
//   „2026 R1 SP03"          → year=2026, release=1, sp=3
//   „2026 R1 SP05 RC1"      → year=2026, release=1, sp=5, spExtra="RC1"
//   „2026 R1 SPn"           → year=2026, release=1, sp=null, spExtra="n"
//   „2023 R1 (2023-03)"     → year=2023, release=1, dateSuffix="2023-03"

export type FixVersionInfo = {
  name:        string;
  year:        number | null;
  release:     number | null;
  sp:          number | null;
  spExtra:     string | null;
  dateSuffix:  string | null;
  count:       number;
};

const RELEASE_RE = /^\s*(\d{4})\s*R(\d+)(?:\s*SP(\d+|n))?(?:\s+(RC\d+|[^()]+?))?(?:\s*\(([^)]+)\))?\s*$/i;

export function parseFixVersion(name: string): Omit<FixVersionInfo, 'count'> {
  const empty: Omit<FixVersionInfo, 'count'> = {
    name, year: null, release: null, sp: null, spExtra: null, dateSuffix: null,
  };
  if (!name) return empty;
  const m = name.match(RELEASE_RE);
  if (!m) return empty;
  const year    = m[1] ? parseInt(m[1], 10) : null;
  const release = m[2] ? parseInt(m[2], 10) : null;
  let sp: number | null       = null;
  let spExtra: string | null  = null;
  if (m[3]) {
    if (/^\d+$/.test(m[3])) sp = parseInt(m[3], 10);
    else                    spExtra = m[3];
  }
  if (m[4]) spExtra = (spExtra ? `${spExtra} ${m[4]}` : m[4]).trim();
  const dateSuffix = m[5] ?? null;
  return { name, year, release, sp, spExtra, dateSuffix };
}

export function inventoryFixVersions(rows: Issue[]): FixVersionInfo[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const fv = (r as Record<string, unknown>)['Fix versions'];
    if (fv == null || fv === '') continue;
    const vals = Array.isArray(fv) ? fv : [fv];
    for (const v of vals) {
      const s = String(v ?? '').trim();
      if (!s) continue;
      counts.set(s, (counts.get(s) ?? 0) + 1);
    }
  }
  const out: FixVersionInfo[] = [];
  for (const [name, count] of counts.entries()) {
    out.push({ ...parseFixVersion(name), count });
  }
  // Sortierung: Year DESC, Release DESC, SP ASC (null=Basis-Release oben,
  // dann SP01, SP02, …), spExtra ASC, dateSuffix ASC, name ASC.
  out.sort((a, b) => {
    const ay = a.year ?? -1, by = b.year ?? -1;
    if (ay !== by) return by - ay;
    const ar = a.release ?? -1, br = b.release ?? -1;
    if (ar !== br) return br - ar;
    // Basis-Release ohne SP kommt zuerst
    const asp = a.sp ?? -1, bsp = b.sp ?? -1;
    if (asp !== bsp) return asp - bsp;
    return a.name.localeCompare(b.name);
  });
  return out;
}

// Filter: alle Rows, die die gewählte Fix-Version in ihrem Array haben.
export function issueHasFixVersion(row: Issue, fixVersion: string): boolean {
  const fv = (row as Record<string, unknown>)['Fix versions'];
  if (fv == null || fv === '') return false;
  const vals = Array.isArray(fv) ? fv : [fv];
  for (const v of vals) {
    if (String(v ?? '').trim() === fixVersion) return true;
  }
  return false;
}
